import {
  IonButton,
  IonButtons,
  IonContent,
  IonDatetime,
  IonHeader,
  IonInput,
  IonModal,
  IonToolbar,
} from "@ionic/react";
import moment from "moment";
import { useContext, useState } from "react";
import { TranslationsContext } from "../util/Translations";
import useTimeFormat from "../hooks/useTimeFormat";

// US 922643: date + time picker that honours the user's 12/24-hour preference.
// Replaces <IonInput type="datetime-local">, whose clock follows the device
// setting and cannot be controlled by the app. The value in and out is the same
// local "YYYY-MM-DDTHH:mm" string (moment.HTML5_FMT.DATETIME_LOCAL) the native
// input used, so callers keep their existing conversions.
type DateTimeInputProps = {
  value?: string;
  onChange: (value?: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

const toLocalValue = (value?: string | string[] | null) => {
  const single = Array.isArray(value) ? value[0] : value;
  if (!single) return undefined;
  const m = moment(single);
  return m.isValid() ? m.format(moment.HTML5_FMT.DATETIME_LOCAL) : undefined;
};

const DateTimeInput: React.FC<DateTimeInputProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
}) => {
  const { translations } = useContext(TranslationsContext);
  const { hourCycle, formatDateTime } = useTimeFormat();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<string | undefined>();

  const open = () => {
    if (disabled) return;
    setDraft(value ?? moment().format(moment.HTML5_FMT.DATETIME_LOCAL));
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const confirm = () => {
    onChange(draft);
    close();
  };

  const clear = () => {
    onChange(undefined);
    close();
  };

  return (
    <>
      <IonInput
        readonly
        disabled={disabled}
        value={formatDateTime(value)}
        placeholder={placeholder}
        onClick={open}
      />
      <IonModal isOpen={isOpen} onIonModalDidDismiss={close}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={close}>
                {translations["lbl_btn_cancel"] || "Cancel"}
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton onClick={clear}>
                {translations["lbl_btn_clear"] || "Clear"}
              </IonButton>
              <IonButton strong onClick={confirm}>
                {translations["lbl_btn_done"] || "Done"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonDatetime
            presentation="date-time"
            hourCycle={hourCycle}
            value={draft}
            onIonChange={(ev) => setDraft(toLocalValue(ev.detail.value))}
          />
        </IonContent>
      </IonModal>
    </>
  );
};

export default DateTimeInput;
